/**
 * Pure parser that turns a raw `EmiratesIdCardData` SignalR payload into
 * the normalized shape the patient form expects. Mirrors the field-level
 * massaging in Flutter `utils.dart:updatePatientDataViaIDScanner`
 * (lines 1353–1502).
 *
 * Pure on purpose — no React, no fetch, no setState. The hook
 * (`use-emirates-id-scan.ts`) is the only impure piece; the parser is
 * trivially unit-testable in `emirates-id-parser.test.ts`.
 *
 * **Caller contract:** pass the lookups (`genderList`, `idTypeList`)
 * that were loaded by `useGenderList()` / `useIdTypeList()`. If a lookup
 * isn't loaded yet (`undefined` / `[]`), the matching field is dropped
 * — better to leave the form alone than to ship a name without the
 * gender ID it pairs with.
 */

import { formatEmiratesId } from '@/patients/components/personal-details/emirates-id-format';
import type { EmiratesIdCardData } from '@/shared/types/emirates-id';

export interface LookupEntry {
  id: string | null;
  name: string | null;
}

export interface NormalizedEmiratesIdScan {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  /** YYYY-MM-DD (matches the form schema). */
  birthDate: string | null;
  /** YYYY-MM-DD. */
  idExpirationDate: string | null;
  /** Formatted `784-YYYY-XXXXXXX-X` to match the masked input. */
  idNumber: string | null;
  /** `{ id, name }` for the "National ID" idType (or first idType if absent). */
  idTypeEntry: LookupEntry | null;
  /** `{ id, name }` resolved against the loaded gender list. */
  genderEntry: LookupEntry | null;
  /** `+971-XXXXXXXXX` form, only set when the scanner returned a phone. */
  phoneNumber: string | null;
  email: string | null;
  /** True when the scanned DOB is more than 18 years ago. */
  isAdult: boolean;
  /** `data:image/jpeg;base64,...` ready to drop into an <img src>. */
  photoDataUrl: string | null;
  /** Raw English nationality label from the card. Resolved to a Nationality
   *  master entry by the caller (async lookup) — the parser stays pure. */
  nationalityEn: string | null;
}

export interface ParseEmiratesIdOptions {
  genderList: readonly LookupEntry[] | null | undefined;
  idTypeList: readonly LookupEntry[] | null | undefined;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ADULT_THRESHOLD_DAYS = 365 * 18 + 1;

export function parseEmiratesIdScan(
  data: EmiratesIdCardData,
  { genderList, idTypeList }: ParseEmiratesIdOptions,
): NormalizedEmiratesIdScan {
  const { firstName, middleName, lastName } = splitFullNameEnglish(data.fullNameEnglish);

  const birthDate = parseScannerDate(data.dateOfBirth);
  const idExpirationDate = parseScannerDate(data.expiryDate);

  const idNumber = data.idNumber ? formatEmiratesId(data.idNumber) : null;

  // ID type: prefer the "National ID" entry; fall back to the first
  // entry if that's somehow missing. Empty / unloaded list ⇒ null.
  const idTypeEntry =
    pickByName(idTypeList ?? [], 'National ID') ??
    firstEntry(idTypeList ?? []);

  // Gender: the scanner sometimes ships "M" / "F" / a word. Normalize
  // to the canonical form, then look up in the loaded list.
  const genderName = normalizeGenderToken(data.gender);
  const genderEntry = genderName ? pickByName(genderList ?? [], genderName) : null;

  return {
    firstName,
    middleName,
    lastName,
    birthDate,
    idExpirationDate,
    idNumber,
    idTypeEntry,
    genderEntry,
    phoneNumber: normalizePhone(data.phone),
    email: data.email,
    isAdult: isBirthDateAdult(birthDate),
    photoDataUrl: data.photo ? `data:image/jpeg;base64,${data.photo}` : null,
    nationalityEn: data.nationalityEn?.trim() || null,
  };
}

function splitFullNameEnglish(full: string | null): {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
} {
  if (!full) return { firstName: null, middleName: null, lastName: null };
  const parts = full
    .replace(/,/g, ' ')
    .split(/\s+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .map(capitalize);

  if (parts.length === 0) return { firstName: null, middleName: null, lastName: null };
  if (parts.length === 1) return { firstName: parts[0], middleName: null, lastName: null };
  if (parts.length === 2) {
    return { firstName: parts[0], middleName: null, lastName: parts[1] };
  }
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}

function capitalize(word: string): string {
  if (word.length === 0) return word;
  return word[0].toUpperCase() + word.slice(1).toLowerCase();
}

function normalizeGenderToken(raw: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > 1) return trimmed;
  switch (trimmed.toUpperCase()) {
    case 'M':
      return 'Male';
    case 'F':
      return 'Female';
    default:
      return 'Other';
  }
}

function parseScannerDate(raw: string | null): string | null {
  if (!raw) return null;
  // Scanner ships `dd/MM/yyyy`. Be lenient about single-digit day / month.
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const day = match[1].padStart(2, '0');
  const month = match[2].padStart(2, '0');
  const year = match[3];
  return `${year}-${month}-${day}`;
}

function normalizePhone(raw: string | null): string | null {
  if (!raw) return null;
  // Flutter: `replaceAll("971", "+971-")` — applies to the FIRST match.
  // JS String.replace with a string pattern also replaces only the first,
  // so behaviour matches.
  return raw.replace('971', '+971-');
}

function pickByName(
  list: readonly LookupEntry[],
  target: string,
): LookupEntry | null {
  const lowered = target.toLowerCase();
  return list.find((e) => e.name?.toLowerCase() === lowered) ?? null;
}

function firstEntry(list: readonly LookupEntry[]): LookupEntry | null {
  return list.length > 0 ? list[0] : null;
}

export function isBirthDateAdult(iso: string | null): boolean {
  if (!iso) return true; // default-adult matches Flutter when DOB absent
  // Accept both `YYYY-MM-DD` (scanner/form) and a full ISO string (a hydrated
  // edit-mode patient) — take the date portion either way.
  const dob = new Date(iso.slice(0, 10) + 'T00:00:00Z').getTime();
  if (Number.isNaN(dob)) return true;
  const threshold = Date.now() - ADULT_THRESHOLD_DAYS * MS_PER_DAY;
  return dob < threshold;
}

/**
 * Routes a scan to the patient identification or guardian identification
 * branch. Mirrors Flutter `utils.dart:1444-1445`:
 *
 *   if ((scanIsAdult && formIsAdult) || (!scanIsAdult && !formIsAdult)) {
 *     // patient branch
 *   } else {
 *     // guardian branch
 *   }
 *
 * In plain English: the scan goes to the *patient* fields only when the
 * scan agrees with the form's current isAdult declaration. Otherwise it
 * goes to the *guardian* fields. That covers the realistic cases:
 *
 *  - Adult patient + adult scan        → patient (the patient scans their card)
 *  - Minor patient + adult scan        → guardian (the parent scans their card)
 *  - Adult patient + minor scan        → guardian (uncommon, but matches Flutter)
 *  - Minor patient + minor scan        → patient (the minor's own card)
 *
 * Re-evaluated on every incoming frame (called from `handleScan`), not
 * captured at mount — covers the case where the user toggles isAdult
 * between scans.
 */
export function pickScanBranch(
  scanIsAdult: boolean,
  formIsAdult: boolean,
): 'patient' | 'guardian' {
  if (scanIsAdult === formIsAdult) return 'patient';
  return 'guardian';
}

/**
 * The adult/minor status the scan should be routed against.
 *
 * Mirrors Flutter `utils.dart:1430-1442`, which recomputes `patient.isAdult`
 * from the child's DOB immediately BEFORE routing. React previously trusted
 * the raw `isAdult` UI toggle (which defaults to Adult and is only changed by
 * an explicit user tap), so a guardian's (adult) scan on a child record whose
 * toggle hadn't been flipped to Minor was misrouted into the CHILD's fields.
 *
 * Fix: derive from the entered birth date when present; fall back to the toggle,
 * then to the scan's own age. Once the child's minor DOB is on the form, an
 * adult guardian scan correctly mismatches → guardian branch, exactly like
 * Flutter — regardless of the toggle.
 */
export function recordIsAdultFromForm(
  birthDate: string | null | undefined,
  formIsAdult: boolean | null | undefined,
  scanIsAdult: boolean,
): boolean {
  if (birthDate && birthDate.trim().length > 0) return isBirthDateAdult(birthDate);
  return formIsAdult ?? scanIsAdult;
}
